/* Pages abonnement — bascule d'intention, choix de la box, rails.

   Quatre règles tiennent tout le fichier :

   · la page connaît son mode dès le rendu, le script ne fait que le changer ;
   · changer de mode change d'URL sans recharger, avec `history.pushState`
     vers l'autre page — le bouton retour du navigateur doit refaire la
     bascule, c'est ce que `popstate` garantit ;
   · rien n'est reconstruit : les deux discours sont déjà dans le document,
     on déplace `hidden` et on réécrit les quelques phrases qui changent ;
   · la vitrine et les bandes éditoriales sont des sections séparées, mais un
     seul script les commande — sinon elles se contrediraient à la bascule.

   Le tunnel de souscription vit dans le même fichier : il partage la racine
   de la vitrine, son mode et ses données. */

(() => {
  const MODES = ['moi', 'offrir'];
  const ZONES = '[data-ltb-abo], [data-ltb-abo-bande]';

  /* Toutes les zones de la page, vitrine et bandes confondues. */
  const zones = () => Array.from(document.querySelectorAll(ZONES));

  const lireCopies = (zone) => {
    const balise = zone.querySelector('[data-ltb-abo-copies]');
    if (!balise) return null;
    try {
      return JSON.parse(balise.textContent);
    } catch (e) {
      // Une copie illisible ne doit pas emporter la bascule : la page reste
      // utilisable, seuls les textes de cette zone ne suivront pas.
      return null;
    }
  };

  /* Un seul balayage décide de tout ce qui se voit. Deux attributs le
     commandent, et un élément peut porter les deux : `data-quand` nomme le
     mode qui le montre, `data-abo-box-si` la box. Les grilles de formules et
     la bande d'appel en dépendent ensemble — deux passes séparées se seraient
     contredites sur elles, la seconde rouvrant ce que la première ferme.

     Les deux noms sont ceux du gabarit, au caractère près : une faute de
     frappe ici ne lève aucune erreur, `dataset` renvoie simplement `undefined`
     et la condition s'annule. Tout resterait visible, les deux box à la fois. */
  const poserVisibilite = (zone, mode, box) => {
    zone.querySelectorAll('[data-quand], [data-abo-box-si]').forEach((el) => {
      const quand = el.dataset.quand;
      const si = el.dataset.aboBoxSi;
      el.hidden = (quand !== undefined && quand !== mode)
        || (si !== undefined && si !== box);
    });
  };

  const poserCopies = (zone, mode) => {
    const copies = lireCopies(zone);
    const jeu = copies && copies[mode];
    if (!jeu) return;
    Object.keys(jeu).forEach((cle) => {
      const el = zone.querySelector(`[data-abo-champ="${cle}"]`);
      // Les copies portent de l'italique sur un mot : `innerHTML` est requis,
      // et la source est le schéma des sections, pas une saisie visiteur.
      if (el) el.innerHTML = jeu[cle];
    });
  };

  /* La section SEO du pied de page n'a pas de marqueur à nous : on la
     retrouve par son bloc, puis on remonte à l'enveloppe que Shopify pose
     autour de chaque section — c'est son identifiant qu'attend l'API de
     rendu, et le coder en dur ici le figerait. */
  const sectionSeo = () => {
    const bloc = document.querySelector('.footer__seo');
    return bloc ? bloc.closest('[id^="shopify-section-"]') : null;
  };

  const poserMode = (mode) => {
    document.documentElement.dataset.ltbAboMode = mode;
    zones().forEach((zone) => {
      zone.dataset.mode = mode;
      poserVisibilite(zone, mode, zone.dataset.box || '');
      poserCopies(zone, mode);
    });
    zones().forEach((zone) => {
      zone.querySelectorAll('[data-abo-mode]').forEach((btn) => {
        btn.setAttribute('aria-selected', String(btn.dataset.aboMode === mode));
      });
    });
  };


  /* ========================================================================
     Mesure

     Un seul point de sortie pour toute la page, et trois destinations, aucune
     obligatoire :

     · `Shopify.analytics.publish` — le bus d'événements personnalisés de
       Shopify. C'est le canal qui survivra au prochain changement d'outil :
       un pixel personnalisé s'y abonne et redistribue vers GA4, Meta ou
       autre chose, sans qu'on retouche le thème.
     · `gtag` — GA4 en direct. La boutique charge déjà `gtag.js` par le canal
       Google & YouTube ; les événements y arrivent sans rien installer.
     · `dataLayer` — **seulement si un conteneur GTM est réellement chargé**.
       `window.dataLayer` existe toujours, c'est gtag qui s'en sert : y pousser
       un `event` sans conteneur ne ferait rien, et avec un conteneur qui
       renvoie lui aussi vers GA4, ferait compter deux fois. On teste donc le
       conteneur, pas le tableau.

     Ce qui n'est pas ici, et volontairement : `add_to_cart`, `begin_checkout`,
     `purchase`. Le canal Google & YouTube les envoie déjà depuis son propre
     pixel, avec les articles et les montants du panier réel ; les réémettre
     doublerait le chiffre d'affaires dans GA4. La page ne mesure donc que ce
     qu'aucun pixel ne peut voir : les intentions, les étapes et les abandons.
     `ajout_au_panier` porte un nom à nous pour cette raison — c'est le repère
     de fin d'entonnoir, pas l'événement e-commerce.

     Une mesure ne doit jamais emporter la page : tout est sous `try`.
     ======================================================================== */

  const gtmCharge = () => {
    const conteneurs = window.google_tag_manager;
    if (!conteneurs) return false;
    return Object.keys(conteneurs).some((cle) => cle.indexOf('GTM-') === 0);
  };

  /* Le tunnel s'enregistre à sa création : le contexte a besoin de son état,
     et la mesure est branchée avant lui. */
  let tunnelActif = null;

  /* Le contexte est recomposé à chaque envoi plutôt que retenu : la bascule,
     l'onglet de box et le tunnel le changent tous, et un contexte figé
     finirait par décrire un écran qu'on a quitté. */
  const contexte = () => {
    const racine = document.querySelector('[data-ltb-abo]');
    const valeurs = {
      mode: (racine && racine.dataset.mode) === 'offrir' ? 'pour_offrir' : 'pour_moi',
      box: racine ? racine.dataset.box : '',
    };
    if (tunnelActif && tunnelActif.el && !tunnelActif.el.hidden) {
      Object.assign(valeurs, tunnelActif.contexte());
    }
    return valeurs;
  };

  const mesurer = (nom, params) => {
    if (!nom) return;
    let charge;
    try {
      charge = Object.assign({}, contexte(), params || {});
    } catch (e) {
      charge = Object.assign({}, params || {});
    }
    // GA4 plafonne à 25 paramètres par événement et n'affiche pas les vides :
    // on ne transmet que ce qui a une valeur.
    Object.keys(charge).forEach((cle) => {
      const v = charge[cle];
      if (v === undefined || v === null || v === '') delete charge[cle];
    });

    try {
      const analytics = window.Shopify && window.Shopify.analytics;
      if (analytics && typeof analytics.publish === 'function') analytics.publish(nom, charge);
    } catch (e) { /* pixel absent, ou en erreur : la page continue */ }
    try {
      // Le tag Google est injecté par le canal Google & YouTube, et pas
      // toujours avant nous : mesuré, `gtag` n'existait pas encore quand
      // l'arrivée sur la page était envoyée, et l'événement se perdait sans
      // trace. On pose donc la file d'attente officielle du snippet Google —
      // celle que `gtag.js` relit à son chargement — au lieu de renoncer.
      if (typeof window.gtag !== 'function') {
        window.dataLayer = window.dataLayer || [];
        window.gtag = function () { window.dataLayer.push(arguments); };
      }
      window.gtag('event', nom, charge);
    } catch (e) { /* gtag absent */ }
    try {
      if (gtmCharge() && Array.isArray(window.dataLayer)) {
        window.dataLayer.push(Object.assign({ event: nom }, charge));
      }
    } catch (e) { /* dataLayer absent */ }
  };

  /* Tous les `data-track` du markup passent par un seul écouteur, posé sur le
     document : les options du tunnel sont écrites par le script après coup, et
     un écouteur posé sur chacune serait à reposer à chaque rendu. */
  const brancherMesure = () => {
    if (document.documentElement.dataset.ltbAboMesure) return;
    document.documentElement.dataset.ltbAboMesure = '1';

    /* En phase de **capture**, et c'est tout l'enjeu : une option du tunnel
       est réécrite par le rendu que son propre clic déclenche. En remontée,
       l'élément cliqué serait déjà détaché du document, `closest(ZONES)` ne
       trouverait plus la page, et le clic ne se mesurerait pas. La capture
       descend avant les gestionnaires du tunnel, le document est intact. */
    document.addEventListener('click', (evt) => {
      if (!evt.target.closest) return;
      const cible = evt.target.closest('[data-track]');
      // Un champ de formulaire se mesure à ce qu'il vaut, pas au clic qui
      // l'ouvre : le sélecteur de pays passe par `change`, plus bas.
      if (!cible || cible.matches('select, input, textarea')) return;
      if (!cible.closest(ZONES)) return;
      mesurer(cible.dataset.track);
    }, true);

    document.addEventListener('change', (evt) => {
      if (!evt.target.closest) return;
      const cible = evt.target.closest('[data-track]');
      if (!cible || !cible.closest(ZONES)) return;
      mesurer(cible.dataset.track, { valeur: cible.value });
    }, true);
  };

  class LtbAbo {
    constructor(racine) {
      this.racine = racine;
      this.brancher();
      // L'état initial doit exister dans l'historique : sans lui, un retour
      // après une bascule quitterait la page au lieu de revenir en arrière.
      history.replaceState({ ltbAboMode: this.mode }, '', location.href);
      document.documentElement.dataset.ltbAboMode = this.mode;
      // Le bloc déjà rendu est celui de la page chargée : on le garde sous la
      // main pour le retour, plutôt que d'aller le rechercher.
      this.seoRendu = new Map();
      this.seoJeton = 0;
      const seo = sectionSeo();
      if (seo) this.seoRendu.set(this.mode, seo.innerHTML);
    }

    get mode() { return this.racine.dataset.mode; }
    get box() { return this.racine.dataset.box; }

    brancher() {
      this.racine.addEventListener('click', (evt) => {
        const bascule = evt.target.closest('[data-abo-mode]');
        if (bascule) return this.changerMode(bascule.dataset.aboMode, true);

        const onglet = evt.target.closest('[data-abo-box]');
        if (onglet) return this.changerBox(onglet.dataset.aboBox);

        const compare = evt.target.closest('[data-abo-compare]');
        if (compare) return this.basculerComparatif();
      });

      window.addEventListener('popstate', (evt) => {
        const mode = (evt.state && evt.state.ltbAboMode) || this.modeDepuisUrl();
        if (mode && mode !== this.mode) this.changerMode(mode, false);
      });
    }

    /* L'URL de l'autre mode garde la requête en cours — les paramètres de
       campagne survivent à la bascule. Un `view=` fait exception : il désigne
       un gabarit précis, et celui de l'autre page n'est pas le même. Sans ce
       report, la bascule en aperçu renverrait vers l'ancienne page. */
    urlDuMode(mode) {
      const base = mode === 'offrir' ? this.racine.dataset.urlOffrir : this.racine.dataset.urlMoi;
      if (!base) return null;
      const cible = new URL(base, location.origin);
      const params = new URLSearchParams(location.search);
      if (params.has('view')) {
        const vue = mode === 'offrir' ? this.racine.dataset.viewOffrir : this.racine.dataset.viewMoi;
        if (vue) params.set('view', vue); else params.delete('view');
      }
      cible.search = params.toString();
      return cible.pathname + (cible.search ? cible.search : '');
    }

    modeDepuisUrl() {
      const cible = this.racine.dataset.urlOffrir;
      if (!cible) return null;
      return location.pathname === new URL(cible, location.origin).pathname ? 'offrir' : 'moi';
    }

    changerMode(mode, pousser) {
      if (!MODES.includes(mode) || mode === this.mode) return;
      poserMode(mode);
      // L'onglet du navigateur suit l'URL. Les deux titres viennent des pages
      // elles-mêmes ; s'ils manquent, on laisse celui du rendu plutôt que
      // d'en inventer un.
      const titre = mode === 'offrir'
        ? this.racine.dataset.titreOffrir
        : this.racine.dataset.titreMoi;
      if (titre) document.title = titre;
      if (pousser) {
        const cible = this.urlDuMode(mode);
        if (cible) history.pushState({ ltbAboMode: mode }, '', cible);
      }
      this.suivreSeo(mode);
    }

    /* Le texte de référencement du bas de page suit la bascule.

       Il ne vient pas de cette section mais du groupe pied de page, rendu par
       le serveur depuis le métaobjet « Contenu SEO » de la page chargée. La
       bascule changeant d'URL sans recharger, ce paragraphe — et le fil
       d'Ariane qui le suit — restaient ceux de l'autre intention.

       Les deux textes ne sont pas rendus côte à côte : ils sont écrits pour le
       référencement, et les poser tous les deux sur les deux URL reviendrait à
       dupliquer chacun d'eux. On va donc chercher le bon au moment du clic,
       par l'API de rendu de section — quatre kilo-octets, une fois par mode.

       En cas d'échec, on laisse ce qui est à l'écran : un texte de l'autre
       intention vaut mieux qu'un trou. */
    suivreSeo(mode) {
      const section = sectionSeo();
      if (!section) return;

      const jeton = ++this.seoJeton;
      const connu = this.seoRendu.get(mode);
      if (connu !== undefined) { section.innerHTML = connu; return; }

      const chemin = this.urlDuMode(mode);
      if (!chemin) return;
      const id = section.id.replace('shopify-section-', '');
      const cible = new URL(chemin, location.origin);
      cible.searchParams.set('sections', id);

      fetch(cible.toString())
        .then((rep) => (rep.ok ? rep.json() : Promise.reject(rep.status)))
        .then((charge) => {
          const html = charge[id];
          if (!html) return;
          const doc = new DOMParser().parseFromString(html, 'text/html');
          const neuf = doc.querySelector('[id^="shopify-section-"]') || doc.body;
          this.seoRendu.set(mode, neuf.innerHTML);
          // Une bascule plus récente a pu passer entre-temps : on ne réécrit
          // que si cet appel est toujours le dernier.
          if (jeton === this.seoJeton && this.mode === mode) section.innerHTML = neuf.innerHTML;
        })
        .catch(() => {});
    }

    changerBox(box) {
      if (!box || box === this.box) return;
      this.racine.dataset.box = box;
      this.racine.querySelectorAll('[data-abo-box]').forEach((btn) => {
        btn.setAttribute('aria-selected', String(btn.dataset.aboBox === box));
      });
      poserVisibilite(this.racine, this.mode, box);
    }

    basculerComparatif() {
      const panneau = this.racine.querySelector('[data-abo-compare-panneau]');
      const lien = this.racine.querySelector('[data-abo-compare]');
      if (!panneau || !lien) return;
      const ouvert = !panneau.hidden;
      panneau.hidden = ouvert;
      lien.setAttribute('aria-expanded', String(!ouvert));
      // Les deux libellés viennent du schéma : on les échange en place plutôt
      // que d'en fabriquer un, pour que « Masquer… » reste modifiable.
      const autre = lien.dataset.libelleAutre;
      if (autre) {
        lien.dataset.libelleAutre = lien.textContent.trim();
        lien.textContent = autre;
      }
    }
  }


  /* ========================================================================
     Tunnel de souscription — pour moi (lot 3)

     L'état tient en cinq valeurs : la formule choisie, le pays, la variante de
     livraison, la première box et l'étape courante. Tout le reste s'en déduit,
     et se recalcule à chaque changement — c'est ce qui garantit que le
     panneau, le récapitulatif et la barre mobile ne peuvent pas se contredire.
     ======================================================================== */

  const euro = (cents) =>
    new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(cents / 100);

  /* Les valeurs d'option Shopify partent dans des attributs HTML : le titre
     d'une box ou d'un format porte des apostrophes, parfois des guillemets. */
  const echapper = (valeur) => String(valeur == null ? '' : valeur)
    .split('&').join('&amp;')
    .split('"').join('&quot;')
    .split('<').join('&lt;')
    .split('>').join('&gt;');

  // Un seuil se dit « 50 € », pas « 50,00 € » — même règle que la fiche box.
  const capitale = (texte) => {
    const t = String(texte || '');
    return t ? t.charAt(0).toUpperCase() + t.slice(1) : '';
  };

  const euroRond = (cents) =>
    cents % 100 === 0
      ? new Intl.NumberFormat('fr-FR', {
        style: 'currency', currency: 'EUR', minimumFractionDigits: 0,
      }).format(cents / 100)
      : euro(cents);

  /* Une même valeur s'affiche parfois à deux endroits : le panneau collant en
     desktop et le tiroir de la barre en mobile. On écrit dans les deux. */
  const ecrire = (racine, selecteur, texte, html) => {
    racine.querySelectorAll(selecteur).forEach((el) => {
      if (html) el.innerHTML = texte; else el.textContent = texte;
    });
  };

  class Tunnel {
    constructor(racine, donnees) {
      this.racine = racine;
      this.d = donnees;
      this.el = racine.querySelector('[data-abo-tunnel]');
      this.etat = {
        formule: null, pays: null, variante: null, premiere: null, etape: 0,
        calendrier: false, calAutre: false,
        format: null, coffret: null, champs: {},
      };
      this.converti = false;
      this.derniereEtapeVue = null;
      this.brancher();
    }

    get formule() { return this.d.formules[this.etat.formule] || null; }

    /* Une carte cadeau n'est pas un abonnement : pas de plan Recharge, pas de
       box du mois, un prix déjà tout compris. Toute la classe s'appuie sur ce
       seul test plutôt que sur le mode de la page — c'est la formule ouverte
       qui décide, et elle seule. */
    get cadeau() { return !!this.formule && this.formule.famille === 'cadeau'; }

    /* Ce que le tunnel ajoute à chaque événement de la page. Le nom de suivi
       de la formule sert d'identifiant plutôt que son titre : c'est celui du
       plan de tracking, et il ne bouge pas quand la copie change. */
    contexte() {
      const f = this.formule;
      if (!f) return {};
      return {
        formule: f.track || f.titre || '',
        formule_famille: f.famille || '',
        etape: this.cles[this.etat.etape] || '',
        etape_num: this.etat.etape + 1 + this.decalage,
      };
    }

    /* Les étapes du tunnel et leur numérotation, qui continue celle de la
       vitrine : « Étape 3 sur 5 » pour soi, « Étape 2 sur 5 » pour offrir —
       le parcours cadeau compte une étape de vitrine au lieu de deux. */
    /* Sans box du mois pour la famille choisie — l'édition Essentielle n'est
       pas toujours publiée — l'étape de la première box n'a rien à proposer.
       Elle disparaît du parcours **et** du compteur : afficher « étape 3 sur
       5 » puis « étape 5 sur 5 » laisse croire qu'on a sauté quelque chose. */
    get sansPremiere() {
      return !this.cadeau && !!this.formule && !this.boxDuMois() && !this.calendrier();
    }

    /* Le calendrier de l'Avent proposé pendant sa vente, au choix avec la box
       du mois (`sections/ltb-abo.liquid`, « calendrier ») : jamais pour un
       cadeau, qui n'ouvre aucun abonnement au nom de l'acheteur. */
    calendrier() {
      if (this.cadeau || !this.formule) return null;
      return this.d.calendrier || null;
    }

    /* Les calendriers retenus et leur prix du jour. Le même arrondi que la
       caisse, mesuré le 21/09 : −15 % sur 59,90 € donne 50,92 €. */
    lignesCalendrier(cal) {
      const deux = this.etat.calAutre && !!cal.autre;
      const taux = deux ? this.d.calRemiseDuo : this.d.calRemise;
      const prixDuJour = (prix) => Math.round((prix * (100 - taux)) / 100);
      const lignes = [{ id: cal.id, titre: cal.titre, prix: cal.prix, promo: prixDuJour(cal.prix), taux }];
      if (deux) {
        lignes.push({
          id: cal.autre.id, titre: cal.autre.titre, prix: cal.autre.prix, promo: prixDuJour(cal.autre.prix), taux,
        });
      }
      return lignes;
    }

    /* L'édition du mois à proposer en supplément. Normalement celle de la box
       choisie ; à défaut celle de l'autre, parce qu'une édition en retard de
       production ne doit pas faire disparaître l'offre pour toute une famille
       de formules. Le repli est marqué : le client doit lire qu'il ajoute une
       autre box que la sienne, et l'étiquette le dit. */
    boxDuMois() {
      const f = this.formule;
      if (!f) return null;
      const sienne = (this.d.boxDuMois || {})[f.box];
      if (sienne) return sienne;
      if (!this.d.repliBoxDuMois) return null;
      const autre = Object.keys(this.d.boxDuMois || {}).find((cle) => cle !== f.box);
      if (!autre) return null;
      return Object.assign({}, this.d.boxDuMois[autre], {
        repli: true,
        familleChoisie: (this.d.nomsFamille || {})[f.box] || '',
      });
    }

    get etapes() {
      const brut = this.cadeau ? this.d.textes.etapesOffrir : this.d.textes.etapesMoi;
      const liste = (brut || '').split(',').map((x) => x.trim()).filter(Boolean);
      if (this.sansPremiere && liste.length === 3) liste.splice(1, 1);
      return liste;
    }

    /* Les étapes franchies dans la vitrine — « ma box », « ma formule ». Le
       tunnel les affiche comme terminées plutôt que de commencer à 3 sans
       expliquer pourquoi : voir qu'on a déjà fait deux pas sur cinq rassure
       plus que de découvrir un compteur qui démarre au milieu. */
    get faits() {
      const brut = this.cadeau ? this.d.textes.faitsOffrir : this.d.textes.faitsMoi;
      return (brut || '').split(',').map((x) => x.trim()).filter(Boolean);
    }

    get decalage() { return this.faits.length; }

    get cles() {
      if (this.cadeau) return ['livraison', 'carte', 'message', 'recap'];
      return this.sansPremiere
        ? ['livraison', 'recap']
        : ['livraison', 'premiere', 'recap'];
    }

    /* Toutes les étapes du gabarit, les deux parcours confondus. Masquer
       seulement celles du parcours courant laissait à l'écran celles de
       l'autre : après un passage par le tunnel cadeau, « Votre message »
       restait sous « Votre livraison » d'un abonnement pour soi. */
    get toutesLesCles() { return ['livraison', 'premiere', 'carte', 'message', 'recap']; }

    brancher() {
      this.racine.addEventListener('click', (evt) => {
        const choix = evt.target.closest('[data-abo-formule]');
        if (choix) return this.ouvrir(choix.dataset.aboFormule, choix);

        const sortie = evt.target.closest('[data-abo-tunnel-sortir]');
        if (sortie) return this.fermer();

        const option = evt.target.closest('[data-tun-choix]');
        if (option) return this.choisir(option);

        if (evt.target.closest('[data-tun-suivant]')) return this.suivant();
        if (evt.target.closest('[data-tun-precedent]')) return this.precedent();
        if (evt.target.closest('[data-tun-barre-bascule]')) return this.basculerDetail();
      });

      // Le bouton retour du navigateur ferme le tunnel plutôt que de quitter
      // la page : c'est le geste attendu quand un écran en recouvre un autre.
      window.addEventListener('popstate', () => { if (!this.el.hidden) this.masquer(); });

      // Quitter le site en plein tunnel est un abandon comme un autre. C'est
      // `pagehide` qu'il faut écouter, pas `beforeunload` : lui seul se
      // déclenche sur mobile, où l'onglet est mis en veille plutôt que fermé.
      window.addEventListener('pagehide', () => {
        if (!this.el.hidden && !this.converti) mesurer('tunnel_abandon', { sortie: 'page_quittee' });
      });

      // Les champs du message se retiennent à la frappe : le retour arrière,
      // celui du navigateur compris, ne doit rien effacer.
      this.el.addEventListener('input', (evt) => {
        const champ = evt.target.closest('[data-tun-champ]');
        if (!champ) return;
        this.etat.champs[champ.dataset.tunChamp] = champ.value;
        this.memoriser();
      });

      /* Une fenêtre modale doit retenir le clavier. `aria-modal` écarte la
         vitrine des lecteurs d'écran, mais pas la tabulation : sans piège, la
         quatrième tabulation quitte le tunnel pour des liens qu'on ne voit
         plus. Et Échap ferme, comme partout ailleurs. */
      this.el.addEventListener('keydown', (evt) => {
        if (evt.key === 'Escape') { evt.preventDefault(); this.fermer(); return; }
        if (evt.key !== 'Tab') return;
        const liste = this.focusables();
        if (!liste.length) return;
        const premier = liste[0];
        const dernier = liste[liste.length - 1];
        const actif = document.activeElement;
        const dehors = !this.el.contains(actif);
        if (evt.shiftKey && (dehors || actif === premier)) {
          evt.preventDefault();
          dernier.focus();
        } else if (!evt.shiftKey && (dehors || actif === dernier)) {
          evt.preventDefault();
          premier.focus();
        }
      });

      // La case du second calendrier : une vraie case à cocher, rendue avec
      // l'étape. Le rendu la recrée, le focus lui est donc rendu ensuite.
      this.el.addEventListener('change', (evt) => {
        const caseAutre = evt.target.closest('[data-tun-cal-autre]');
        if (!caseAutre) return;
        this.etat.calAutre = caseAutre.checked;
        mesurer('upsell_calendrier_second', { ajout: caseAutre.checked ? 'oui' : 'non' });
        this.rendre();
        const neuve = this.el.querySelector('[data-tun-cal-autre]');
        if (neuve) neuve.focus({ preventScroll: true });
      });

      const pays = this.el.querySelector('[data-tun-pays]');
      if (pays) {
        pays.addEventListener('change', () => {
          this.etat.pays = pays.value;
          // Changer de pays change la grille : la variante retenue peut ne
          // plus exister. On reprend la première du nouveau pays.
          const liste = this.optionsDuPays();
          this.etat.variante = liste.length ? liste[0].id : null;
          this.rendre();
        });
      }
    }

    /* ---------------- Ouverture et fermeture ---------------- */

    async ouvrir(idBloc, ouvreur) {
      if (!this.d.formules[idBloc]) return;
      // À la fermeture, le focus revient d'où il vient : sans ça il repart au
      // début du document, très loin des formules qu'on venait de comparer.
      this.ouvreur = ouvreur || null;
      this.etat.formule = idBloc;
      this.etat.premiere = null;
      this.etat.calendrier = false;
      this.etat.calAutre = false;
      this.etat.format = null;
      this.etat.coffret = null;
      this.etat.etape = 0;

      const liste = this.formule.variantes.filter((v) => v.dispo);
      const premier = liste[0];
      this.etat.pays = premier ? this.paysEclates(premier.pays)[0] : null;
      this.etat.variante = premier ? premier.id : null;
      this.reprendre();

      this.el.hidden = false;
      document.documentElement.style.overflow = 'hidden';
      history.pushState({ ltbAboMode: this.racine.dataset.mode, ltbAboTunnel: true }, '', location.href);
      this.pousse = true;
      // L'entrée dans le tunnel est un événement à part : le clic sur la
      // carte de formule est déjà mesuré, mais il ne dit pas si le tunnel
      // s'est réellement ouvert — une formule sans variante disponible sort
      // plus haut. Il part **avant** le premier rendu : sinon « étape vue »
      // le précéderait, et la remise à zéro effacerait la première étape,
      // qui se recompterait au premier clic d'option.
      this.converti = false;
      this.derniereEtapeVue = null;
      mesurer('tunnel_ouvert');

      this.el.scrollTop = 0;
      this.rendre();

      // Recharge n'accepte qu'un abonnement par commande. On le dit à
      // l'entrée, pas après cinq étapes remplies. Une carte cadeau échappe à
      // la règle : elle n'ouvre aucun abonnement au nom de l'acheteur.
      if (!this.cadeau && await this.abonnementDejaAuPanier()) this.bloquer();
    }

    /* `offsetParent` ne convient pas ici : il vaut `null` pour tout élément
       en `position: fixed`, et la barre récapitulative du mobile en est une.
       On teste donc les rectangles rendus, qui disent la vérité dans les deux
       cas. */
    focusables() {
      const selecteur = 'a[href], button:not([disabled]), select:not([disabled]),'
        + ' input:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';
      return Array.from(this.el.querySelectorAll(selecteur))
        .filter((el) => el.getClientRects().length > 0);
    }

    /* Le focus suit l'étape. Sans ça, « Continuer » fait défiler l'écran mais
       laisse le clavier sur un bouton qui a changé de sens, et le lecteur
       d'écran n'annonce rien. `preventScroll` : c'est le tunnel qui décide du
       défilement, pas le navigateur. */
    poserFocus(cle) {
      const section = this.el.querySelector(`[data-tun-etape="${cle}"]`);
      const titre = section && section.querySelector('.ltb-tun__titre');
      if (titre) titre.focus({ preventScroll: true });
    }

    fermer() {
      // On repasse par l'historique pour ne pas y laisser une entrée morte :
      // `popstate` se chargera de masquer.
      if (this.pousse) { this.pousse = false; history.back(); return; }
      this.masquer();
    }

    masquer() {
      // Un tunnel quitté sans ajout au panier est un abandon, et l'étape
      // atteinte est toute l'information : elle dit où le parcours casse.
      // L'événement part avant `hidden`, sinon le contexte serait déjà vide.
      if (!this.el.hidden && !this.converti) mesurer('tunnel_abandon');
      this.pousse = false;
      this.el.hidden = true;
      document.documentElement.style.overflow = '';
      this.el.querySelector('[data-tun-barre-detail]').hidden = true;
      if (this.ouvreur && document.contains(this.ouvreur)) {
        this.ouvreur.focus({ preventScroll: true });
        this.ouvreur = null;
      }
    }

    /* Les choix survivent à une sortie du tunnel : revenir aux formules puis
       en rouvrir une ne doit pas faire ressaisir le pays. L'étape, elle, repart
       du début — reprendre au milieu d'un parcours qu'on a quitté désoriente
       plus que ça n'aide. */
    memoriser() {
      try {
        sessionStorage.setItem('ltb-abo-tunnel', JSON.stringify({
          formule: this.etat.formule,
          pays: this.etat.pays,
          variante: this.etat.variante,
          premiere: this.etat.premiere,
          calendrier: this.etat.calendrier,
          calAutre: this.etat.calAutre,
          format: this.etat.format,
          coffret: this.etat.coffret,
          champs: this.etat.champs,
        }));
      } catch (e) { /* navigation privée, quota : la mémoire est un confort */ }
    }

    reprendre() {
      let garde = null;
      try { garde = JSON.parse(sessionStorage.getItem('ltb-abo-tunnel') || 'null'); } catch (e) { garde = null; }
      if (!garde || garde.formule !== this.etat.formule) return;
      if (garde.pays) this.etat.pays = garde.pays;
      if (garde.variante) this.etat.variante = garde.variante;
      if (garde.premiere !== undefined) this.etat.premiere = garde.premiere;
      if (garde.calendrier !== undefined) this.etat.calendrier = !!garde.calendrier;
      if (garde.calAutre !== undefined) this.etat.calAutre = !!garde.calAutre;
      if (garde.format) this.etat.format = garde.format;
      if (garde.coffret) this.etat.coffret = garde.coffret;
      if (garde.champs) this.etat.champs = garde.champs;
    }

    async abonnementDejaAuPanier() {
      try {
        const reponse = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
        if (!reponse.ok) return false;
        const panier = await reponse.json();
        return (panier.items || []).some((ligne) => ligne.selling_plan_allocation);
      } catch (e) {
        // Panier illisible : on laisse passer. Un faux blocage coûterait plus
        // cher qu'un doublon, que Shopify refusera de toute façon au paiement.
        return false;
      }
    }

    bloquer() {
      // Un parcours arrêté d'entrée n'est pas un abandon : c'est une règle de
      // Recharge qui s'applique. Le distinguer évite de lire un trou dans
      // l'entonnoir là où il n'y en a pas.
      mesurer('tunnel_bloque');
      this.toutesLesCles.forEach((cle) => {
        const section = this.el.querySelector(`[data-tun-etape="${cle}"]`);
        if (section) section.hidden = true;
      });
      this.el.querySelector('[data-tun-etape="deja"]').hidden = false;
      this.el.querySelector('[data-tun-panneau]').hidden = true;
      this.el.querySelector('[data-tun-barre]').hidden = true;
      // Le compteur entier disparaît, pas seulement son contenu : une barre
      // vide entre l'en-tête et le message n'aurait rien signifié.
      this.el.querySelector('.ltb-tun__pas').hidden = true;
      this.el.querySelector('[data-tun-pas]').innerHTML = '';
      this.poserFocus('deja');
    }

    /* ---------------- Navigation ---------------- */

    suivant() {
      if (this.etat.etape < this.cles.length - 1) {
        mesurer('tunnel_etape_suivante');
        this.etat.etape += 1;
        this.rendre();
        this.el.scrollTo({ top: 0, behavior: 'smooth' });
      } else {
        this.ajouterAuPanier();
      }
    }

    precedent() {
      if (this.etat.etape > 0) {
        mesurer('tunnel_etape_precedente');
        this.etat.etape -= 1;
        this.rendre();
        this.el.scrollTo({ top: 0, behavior: 'smooth' });
      } else {
        this.fermer();
      }
    }

    basculerDetail() {
      const detail = this.el.querySelector('[data-tun-barre-detail]');
      const bouton = this.el.querySelector('[data-tun-barre-bascule]');
      const ouvert = !detail.hidden;
      detail.hidden = ouvert;
      bouton.setAttribute('aria-expanded', String(!ouvert));
      const autre = bouton.dataset.libelleAutre;
      if (autre) {
        bouton.dataset.libelleAutre = bouton.textContent.trim();
        bouton.textContent = autre;
      }
    }

    choisir(bouton) {
      const quoi = bouton.dataset.tunChoix;
      const valeur = bouton.dataset.tunValeur;
      if (quoi === 'livraison') this.etat.variante = Number(valeur);
      // Trois choix exclusifs à l'étape de la première box : la box du mois,
      // le calendrier, ou rien. `premiere` garde son sens d'origine — la box
      // du mois est prise — pour tout ce qui le lisait déjà.
      if (quoi === 'premiere') {
        this.etat.premiere = valeur === 'oui';
        this.etat.calendrier = valeur === 'calendrier';
      }
      if (quoi === 'format') {
        this.etat.format = valeur;
        // Quitter le coffret oublie la box choisie : la garder ferait
        // repartir une propriété de ligne sans coffret pour la porter.
        if (!this.formatCourant() || !this.formatCourant().coffret) this.etat.coffret = null;
      }
      if (quoi === 'coffret') this.etat.coffret = valeur;
      this.rendre();
    }

    /* ---------------- Grille de livraison ----------------

       Shopify range Pays-Bas, Allemagne et Espagne sur une seule variante ;
       le sélecteur en propose trois, qui pointent vers elle. Le découpage se
       fait sur la donnée, pas sur une liste écrite en dur. */

    paysEclates(valeur) { return String(valeur || '').split(' - ').map((s) => s.trim()).filter(Boolean); }

    listePays() {
      const vus = [];
      (this.formule ? this.formule.variantes : []).forEach((v) => {
        if (!v.dispo) return;
        this.paysEclates(v.pays).forEach((p) => { if (!vus.includes(p)) vus.push(p); });
      });
      return vus;
    }

    /* Une carte cadeau croise deux options : onze livraisons × quatre cartes,
       soit quarante-quatre variantes pour trois modes réels. L'étape de
       livraison ne doit en montrer qu'un par mode, sans quoi le même « Point
       relais » apparaîtrait quatre fois. */
    optionsDuPays() {
      if (!this.formule) return [];
      const vus = [];
      return this.formule.variantes.filter((v) => {
        if (!v.dispo || !this.paysEclates(v.pays).includes(this.etat.pays)) return false;
        const cle = `${v.pays}|${v.mode}`;
        if (vus.includes(cle)) return false;
        vus.push(cle);
        return true;
      });
    }

    /* La variante retenue à l'étape de livraison. Pour un abonnement c'est
       celle qu'on ajoutera au panier ; pour un cadeau elle ne désigne encore
       que la livraison, le format de carte reste à choisir. */
    variante() {
      const liste = this.optionsDuPays();
      return liste.filter((v) => v.id === this.etat.variante)[0] || liste[0] || null;
    }

    /* Celle qui part réellement au panier : livraison et format croisés. */
    varianteReelle() {
      const base = this.variante();
      if (!base) return null;
      if (!this.cadeau) return base;
      if (!this.etat.format) return null;
      return this.formule.variantes.filter(
        (v) => v.dispo && v.pays === base.pays && v.mode === base.mode && v.carte === this.etat.format,
      )[0] || null;
    }

    /* Les formats réellement vendables pour la livraison choisie. Un format
       du schéma qui ne correspond à aucune variante disparaît — c'est ce qui
       écarte l'option « Non Disponible » du catalogue sans l'écrire ici. */
    formatsDisponibles() {
      const base = this.variante();
      if (!base || !this.cadeau) return [];
      const trouves = [];
      (this.d.formatsCarte || []).forEach((f) => {
        const v = this.formule.variantes.filter(
          (x) => x.dispo && x.pays === base.pays && x.mode === base.mode && x.carte === f.option,
        )[0];
        if (!v) return;
        // Un format papier sans tarif pour ce pays mène à un paiement que
        // Shopify refusera faute de mode d'expédition : mieux vaut ne pas le
        // proposer que de laisser le client buter à la caisse.
        const complet = { ...f, id: v.id, prix: v.prix };
        if (f.profil && f.profil !== 'aucun' && !this.portDuFormat(complet)) return;
        // Un format peut être réservé à certains pays. Le coffret l'est à la
        // France et à la Belgique : ailleurs son contenu compte comme un
        // second colis chez Shopify et le client paierait deux livraisons
        // pour un seul carton — mesuré à 25,80 € vers la Suisse.
        if (!this.paysAutorise(f)) return;
        trouves.push(complet);
      });
      return trouves;
    }

    formatCourant() {
      return this.formatsDisponibles().filter((f) => f.option === this.etat.format)[0] || null;
    }

    paysAutorise(format) {
      const brut = (format.pays || '').trim();
      if (!brut) return true;
      return brut.split(',').map((x) => x.trim()).filter(Boolean).includes(this.etat.pays);
    }

    /* Le contenu retenu pour le coffret, s'il y a lieu. */
    contenuCourant() {
      if (!this.etat.coffret) return null;
      return (this.d.coffretBoxes || []).filter((b) => String(b.id) === String(this.etat.coffret))[0] || null;
    }

    /* Ce que coûte l'envoi du format choisi, depuis nos bureaux. La carte par
       e-mail n'a pas de profil : rien ne part, rien ne se facture. */
    portDuFormat(format) {
      if (!format || !format.profil || format.profil === 'aucun') return null;
      const grille = (this.d.livraisonCadeau || {})[format.profil];
      if (!grille) return null;
      return grille[this.etat.pays] || null;
    }

    /* ---------------- Calculs ----------------

       Deux familles, deux façons de compter. Mensuelle : la base et le port
       tombent chaque mois. Prépayée : la base couvre la période entière, le
       port se multiplie par le nombre de mois. La box du mois, elle, ne se
       paie qu'une fois. */

    montants() {
      const f = this.formule;
      const v = this.variante();
      const port = v ? v.port : 0;
      if (this.cadeau) return this.montantsCadeau(f, port);
      const prepaye = f.famille === 'prepaye';
      const cycle = prepaye ? f.cycle : 1;
      const bm = this.boxDuMois();
      const prise = this.etat.premiere === true && !!bm;
      const extra = prise ? bm.promo : 0;
      const boxLiv = prise ? this.portDeLaBox(bm.promo) : { port: 0, offerte: false, note: '' };
      // Le calendrier ne peut pas être pris avec la box du mois : le choix est
      // exclusif, et la garde le redit ici pour les totaux.
      const cal = this.calendrier();
      const calPris = !prise && this.etat.calendrier === true && !!cal;
      const calLignes = calPris ? this.lignesCalendrier(cal) : [];
      const calTotal = calLignes.reduce((somme, l) => somme + l.promo, 0);
      // Les calendriers relèvent du profil de la boutique, comme la box du
      // mois : même grille, même seuil de gratuité, compté sur leur seul prix.
      const calLiv = calPris ? this.portDeLaBox(calTotal) : { port: 0, offerte: false, note: '' };
      const recurrent = prepaye ? f.base + port * cycle : f.base + port;
      return {
        port, prepaye, cycle, bm, extra, recurrent, boxLiv,
        cal, calPris, calLignes, calTotal, calLiv,
        aujourdhui: recurrent + extra + (boxLiv.port || 0) + calTotal + (calLiv.port || 0),
        // Pays sans grille boutique : le total annonce un plancher.
        inconnu: boxLiv.port === null || calLiv.port === null,
      };
    }

    /* Le prix d'une variante cadeau est tout compris — abonnement, port des N
       mois et supplément de carte confondus. Le récapitulatif, lui, doit les
       montrer séparément : on redécompose depuis le prix affiché plutôt que
       de saisir trois nombres qui finiraient par se contredire.

       Le supplément se lit comme l'écart au format le moins cher **de la même
       livraison** — comparer à une autre livraison ferait passer un port pour
       un supplément de carte. */
    montantsCadeau(f, port) {
      const cycle = f.cycle || 1;
      const formats = this.formatsDisponibles();
      const prix = formats.map((x) => x.prix);
      const plancher = prix.length ? Math.min.apply(null, prix) : f.base;
      const choisi = this.formatCourant();
      const total = choisi ? choisi.prix : plancher;
      const supplement = choisi ? choisi.prix - plancher : 0;
      // Le port du format papier n'est pas dans le prix de la variante : il
      // vient d'un profil de livraison à part et s'ajoute au paiement.
      const envoi = this.portDuFormat(choisi);
      const contenu = choisi && choisi.coffret ? this.contenuCourant() : null;
      return {
        cadeau: true, port, prepaye: false, cycle, bm: null, extra: 0,
        boxLiv: { port: 0, offerte: false, note: '' }, inconnu: false,
        supplement,
        envoi,
        contenu,
        livraison: port * cycle,
        base: total - supplement - port * cycle,
        recurrent: total,
        aujourdhui: total + (envoi ? envoi.port : 0) + (contenu ? contenu.promo : 0),
      };
    }

    /* La box du mois n'est pas un abonnement. Elle part dans son propre colis,
       relève du profil de livraison de la boutique, et Shopify additionne les
       deux ports au paiement — mesuré : 2,90 € + 1,90 € = 4,80 € pour la
       France. La fiche produit box compte déjà ainsi ; l'omettre ici ferait
       annoncer deux totaux différents pour le même panier. */
    portDeLaBox(prix) {
      const pays = (this.d.livraisonBoutique || {})[this.etat.pays];
      if (!pays) return { port: null, offerte: false, note: '' };
      const offerte = pays.seuil !== null && prix >= pays.seuil;
      let note = '';
      if (pays.seuil !== null) {
        note = offerte
          ? `Offerte : vous dépassez ${euroRond(pays.seuil)} d’achats boutique`
          : `Offerte dès ${euroRond(pays.seuil)} d’achats boutique`;
      }
      return { port: offerte ? 0 : pays.port, offerte, note };
    }

    /* Le prix de la livraison de la box, tel qu'il s'écrit dans une colonne. */
    prixPortBox(m) { return this.prixPort(m.boxLiv); }

    prixPort(liv) {
      const t = this.d.textes;
      if (liv.port === null) return t.portAuPaiement;
      return liv.offerte ? 'Offerte' : euro(liv.port);
    }

    aujourdhuiTexte(m) {
      return m.inconnu ? `à partir de ${euro(m.aujourdhui)}` : euro(m.aujourdhui);
    }

    /* Les textes du schéma portent des jetons : le mois de départ, le nom de
       la box, le taux de remise. Ils se remplacent au même endroit pour tout
       le tunnel — sinon l'étape et le récapitulatif finiraient par diverger. */
    jetons(texte, m) {
      return (texte || '')
        .split('[mois]').join(this.d.moisDepart)
        .split('[mois_court]').join(this.d.moisDepartCourt)
        .split('[mois_vente]').join(this.d.textes.moisVente || '')
        .split('[de_mois_vente]').join(this.d.textes.moisVenteElide || '')
        .split('[box]').join(m && m.bm ? m.bm.titre : '')
        .split('[nom_box]').join(m && m.bm ? m.bm.famille || '' : '')
        .split('[nom_famille]').join(m && m.bm ? m.bm.familleChoisie || '' : '')
        .split('[remise]').join(String(this.d.remise))
        .split('[n]').join(String(m && m.cycle ? m.cycle : ''))
        .split('[quand]').join(m && m.bm ? m.bm.precommandeQuand || '' : '')
        .split('[mois_capitale]').join(capitale(this.d.moisDepartCourt))
        .split('[calendrier]').join(this.d.calendrier ? this.d.calendrier.titre : '')
        .split('[autre]').join(this.d.calendrier && this.d.calendrier.autre ? this.d.calendrier.autre.titre : '')
        .split('[quand_cal]').join(this.d.calendrier ? this.d.calendrier.quand || '' : '')
        .split('[remise_cal]').join(String(this.d.calRemise))
        .split('[remise_duo]').join(String(this.d.calRemiseDuo));
    }

    puis(m) {
      const f = this.formule;
      // Un cadeau ne se renouvelle pas, et la page l'a déjà dit trois fois :
      // sur l'encart des formules, sous le prix, et dans la mention de
      // renouvellement. Une quatrième ne rassure plus, elle alourdit.
      if (m.cadeau) return '';
      if (m.prepaye) return `${euro(m.recurrent)} tous les ${m.cycle} mois`;
      if (f.famille === 'mensuel_engage') {
        return `${euro(m.recurrent)}/mois · engagement 12 mois, puis sans engagement au même tarif`;
      }
      return `${euro(m.recurrent)}/mois`;
    }

    renouvellement(m) {
      const t = this.d.textes;
      if (m.cadeau) return (t.renouvCadeau || '').split('[n]').join(String(m.cycle));
      if (m.prepaye) return (t.renouvPrepaye || '').split('[n]').join(String(m.cycle));
      if (this.formule.famille === 'mensuel_engage') return t.renouvEngage || '';
      return t.renouvMensuel || '';
    }

    /* ---------------- Rendu ---------------- */

    rendre() {
      if (!this.formule) return;
      const f = this.formule;
      const m = this.montants();
      const t = this.d.textes;
      const cleEtape = this.cles[this.etat.etape];

      this.toutesLesCles.forEach((cle) => {
        const section = this.el.querySelector(`[data-tun-etape="${cle}"]`);
        if (section) section.hidden = cle !== cleEtape;
      });
      this.el.querySelector('[data-tun-etape="deja"]').hidden = true;
      this.el.querySelector('.ltb-tun__pas').hidden = false;
      this.el.querySelector('[data-tun-panneau]').hidden = false;
      this.el.querySelector('[data-tun-barre]').hidden = false;

      // Une étape ne se compte qu'à sa première apparition : `rendre()` est
      // rappelé à chaque clic d'option, et compter chaque rendu ferait passer
      // une hésitation sur la livraison pour cinq visites de l'étape.
      if (cleEtape && cleEtape !== this.derniereEtapeVue) {
        this.derniereEtapeVue = cleEtape;
        mesurer('tunnel_etape_vue');
        this.poserFocus(cleEtape);
      }

      this.rendreCompteur();
      if (cleEtape === 'livraison') this.rendreLivraison();
      if (cleEtape === 'premiere') this.rendrePremiere(m);
      if (cleEtape === 'carte') this.rendreCarte();
      if (cleEtape === 'message') this.rendreMessage();
      if (cleEtape === 'recap') this.rendreRecap(m);

      // Panneau et barre : les mêmes valeurs, écrites aux deux endroits.
      ecrire(this.el, '[data-tun-formule-titre]', f.titre);
      ecrire(this.el, '[data-tun-formule-sous]', this.sousTitre(m));
      ecrire(this.el, '[data-tun-aujourdhui]', this.aujourdhuiTexte(m));
      const suite = this.puis(m);
      ecrire(this.el, '[data-tun-puis]', suite);
      this.el.querySelectorAll('[data-tun-bloc-puis]').forEach((el) => { el.hidden = !suite; });
      ecrire(this.el, '[data-tun-renouv]', this.renouvellement(m));
      this.rendreGages();
      this.rendreLignes(m);

      this.memoriser();

      const dernier = this.etat.etape === this.cles.length - 1;
      ecrire(this.el, '[data-tun-suivant]', dernier ? t.ajouter : t.suivant);
      // Aucune option payante n'est pré-cochée : tant que le format n'est pas
      // choisi, « Continuer » ne mène nulle part et le dit.
      const manque = this.manquant(cleEtape);
      this.el.querySelectorAll('[data-tun-suivant]').forEach((b) => { b.disabled = !!manque; });
      this.el.querySelector('[data-tun-precedent]').hidden = this.etat.etape === 0;
    }

    /* Ce qui empêche de passer à l'étape suivante, s'il y a lieu. */
    manquant(cleEtape) {
      if (cleEtape === 'carte') {
        if (!this.etat.format) return 'format';
        const f = this.formatCourant();
        if (f && f.coffret && !this.etat.coffret && (this.d.coffretBoxes || []).length) return 'coffret';
      }
      if (cleEtape === 'recap' && this.cadeau && !this.varianteReelle()) return 'variante';
      return null;
    }

    sousTitre(m) {
      const f = this.formule;
      // « 6 mois offerts » se lit « 6 mois gratuits » : ce n'est pas ce qu'on
      // vend. Le mot « cadeau » lève l'ambiguïté sans rien perdre.
      if (m.cadeau) {
        const sous = (this.d.textes.cadeauSousTitre || 'cadeau [n] mois').split('[n]').join(String(m.cycle));
        return `${f.nomBox} · ${sous}`;
      }
      if (m.prepaye) return `${f.nomBox} · réglé en une fois`;
      if (f.famille === 'mensuel_engage') return `${f.nomBox} · engagement 12 mois`;
      return `${f.nomBox} · sans engagement`;
    }

    /* « Les box sont expédiées entre le 5 et le 10 » ne veut rien dire quand
       on achète une carte cadeau qu'on reçoit tout de suite : les deux jeux de
       gages existent, le tunnel prend celui de la formule ouverte. */
    rendreGages() {
      const t = this.d.textes;
      const brut = this.cadeau ? t.gagesCadeau : t.gagesMoi;
      const liste = (brut || '').split('|').map((x) => x.trim()).filter(Boolean);
      const coche = '<svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true">'
        + '<path d="M2 7.4 5.4 11 12 3.6" stroke="currentColor" stroke-width="1.4"'
        + ' stroke-linecap="round" stroke-linejoin="round"/></svg>';
      const html = liste.map((g) => `<li class="ltb-tun__gage">${coche}${echapper(g)}</li>`).join('');
      this.el.querySelectorAll('[data-tun-gages]').forEach((ul) => { ul.innerHTML = html; });
    }

    rendreCompteur() {
      const liste = this.el.querySelector('[data-tun-pas]');
      const faits = this.faits;
      const total = this.etapes.length + faits.length;

      /* Le parcours entier, vitrine comprise. Les étapes de la vitrine ne sont
         pas cliquables : elles vivent sous le tunnel, y revenir voudrait dire
         le fermer. Elles sont là pour situer, pas pour naviguer. */
      const tout = faits.map((label) => ({ label, etat: ' data-faite' }))
        .concat(this.etapes.map((label, i) => ({
          label,
          etat: i === this.etat.etape ? ' data-courant' : (i < this.etat.etape ? ' data-faite' : ''),
        })));

      liste.innerHTML = tout.map((pas, i) => {
        const trait = i < tout.length - 1 ? '<li class="ltb-tun__trait" aria-hidden="true"></li>' : '';
        return `<li class="ltb-tun__pas-item"${pas.etat}>
            <span class="ltb-tun__pastille">${i + 1}</span>${pas.label}
          </li>${trait}`;
      }).join('');

      const jauge = this.el.querySelector('[data-tun-jauge]');
      jauge.hidden = false;
      jauge.querySelector('[data-tun-jauge-label]').textContent = this.etapes[this.etat.etape] || '';
      jauge.querySelector('[data-tun-jauge-compte]').textContent =
        `Étape ${this.etat.etape + 1 + this.decalage} sur ${total}`;
      // Une échelle plutôt qu'une largeur : la barre avance sans recalcul de
      // mise en page à chaque étape.
      jauge.querySelector('[data-tun-jauge-barre]').style.transform =
        `scaleX(${((this.etat.etape + 1 + this.decalage) / total).toFixed(3)})`;
    }

    rendreLivraison() {
      const t = this.d.textes;
      const m = this.montants();

      /* Pour soi, on choisit sa propre livraison ; pour un cadeau, celle de
         quelqu'un d'autre, qui donnera son adresse plus tard. Ce n'est pas la
         même question, et le pays engage en plus le tarif de toute la période
         — la mention le dit sous le champ, au moment du choix. */
      ecrire(this.el, '[data-tun-liv-titre]', this.cadeau ? t.livTitreCadeau : t.livTitre, true);
      ecrire(this.el, '[data-tun-liv-intro]', this.cadeau ? t.livIntroCadeau : t.livIntro, true);
      const note = this.el.querySelector('[data-tun-liv-note]');
      if (note) {
        note.textContent = this.cadeau ? (t.livNoteCadeau || '') : '';
        note.hidden = !this.cadeau || !t.livNoteCadeau;
      }

      const select = this.el.querySelector('[data-tun-pays]');
      const pays = this.listePays();
      select.innerHTML = pays.map((p) => `<option value="${p}">${p}</option>`).join('');
      select.value = this.etat.pays;

      const active = this.variante();
      this.el.querySelector('[data-tun-modes]').innerHTML = this.optionsDuPays().map((v) => `
        <button type="button" class="ltb-tun__option" role="radio"
          aria-checked="${active && v.id === active.id}"
          data-tun-choix="livraison" data-tun-valeur="${v.id}" data-track="tunnel_mode_livraison">
          <span class="ltb-tun__radio" aria-hidden="true"></span>
          <span class="ltb-tun__option-texte">
            <span class="ltb-tun__option-nom">${v.mode}</span>
            <span class="ltb-tun__option-detail">Livré en ${v.delai}</span>
          </span>
          <span class="ltb-tun__option-prix${this.cadeau ? ' ltb-tun__option-prix--long' : ''}">${this.prixLivraison(v, m)}</span>
        </button>`).join('');
    }

    /* « 1,90 €/mois » sur une formule cadeau contredit « aucun prélèvement » :
       tout est réglé aujourd'hui. Le même montant se dit donc pour la période
       — « 11,40 € pour les 6 box », et le prix unitaire en dessous, qui reste
       le repère de comparaison entre les modes de livraison. */
    prixLivraison(v, m) {
      const t = this.d.textes;
      if (!this.cadeau) return `${euro(v.port)}/mois`;
      const cycle = m.cycle || 1;
      const total = (t.cadeauPortTotal || '[total] pour les [n] box')
        .split('[total]').join(euro(v.port * cycle))
        .split('[n]').join(String(cycle));
      const unite = (t.cadeauPortUnite || '').split('[prix]').join(euro(v.port));
      return `${total}${unite ? `<small class="ltb-tun__option-unite">${unite}</small>` : ''}`;
    }

    rendrePremiere(m) {
      const t = this.d.textes;
      const zone = this.el.querySelector('[data-tun-premiere]');
      const faitBox = this.el.querySelector('[data-tun-pb-box]');
      const faitEnvoi = this.el.querySelector('[data-tun-pb-envoi]');
      const relance = this.el.querySelector('[data-tun-pb-relance]');

      // Ni box du mois ni calendrier en vente : l'étape n'a rien à proposer.
      if (!m.bm && !m.cal) {
        this.etat.premiere = false;
        this.etat.calendrier = false;
        this.etat.etape = this.cles.indexOf('recap');
        return this.rendre();
      }

      const jetons = (texte) => this.jetons(texte, m);

      if (faitBox) faitBox.innerHTML = jetons(t.pbFaitBox);
      if (faitEnvoi) faitEnvoi.innerHTML = jetons(t.pbFaitEnvoi);
      // La relance parle de la box du mois : sans elle, elle se tait.
      if (relance) {
        relance.innerHTML = m.bm ? jetons(t.pbRelance) : '';
        relance.hidden = !m.bm;
      }
      const choixBox = m.bm ? `
        <button type="button" class="ltb-tun__option" role="radio"
          aria-checked="${this.etat.premiere === true}"
          data-tun-choix="premiere" data-tun-valeur="oui" data-track="upsell_box_du_mois_accepte">
          <span class="ltb-tun__radio" aria-hidden="true"></span>
          <img class="ltb-tun__vignette" src="${m.bm.image}" alt="" loading="lazy" width="88" height="88">
          <span class="ltb-tun__option-texte">
            ${t.pbOuiKicker ? `<span class="ltb-tun__option-kicker">${jetons(t.pbOuiKicker)}</span>` : ''}
            <span class="ltb-tun__option-nom">${jetons(t.pbOui)}</span>
            ${m.bm.accroche ? `<span class="ltb-tun__option-accroche">${echapper(m.bm.accroche)}</span>` : ''}
            <span class="ltb-tun__option-detail">${m.bm.precommande ? '' : jetons(t.pbOuiTexte)}</span>
            ${m.bm.repli ? `<span class="ltb-tun__option-repli">${jetons(t.pbRepli)}</span>` : ''}
          </span>
          <span class="ltb-tun__option-prix">${euro(m.bm.promo)}
            <span class="ltb-tun__option-avant">
              <s class="ltb-tun__option-plein">${euro(m.bm.prix)}</s>
              ${t.pbOuiRemise ? `<span class="ltb-tun__option-remise">${jetons(t.pbOuiRemise)}</span>` : ''}
            </span>
            ${m.bm.precommande ? `<span class="ltb-precom">${jetons(t.precommandeMessage)}</span>` : ''}
          </span>
        </button>` : '';
      zone.innerHTML = `${choixBox}${this.htmlChoixCalendrier(m)}
        <button type="button" class="ltb-tun__option" role="radio"
          aria-checked="${this.etat.premiere === false && this.etat.calendrier !== true}"
          data-tun-choix="premiere" data-tun-valeur="non" data-track="upsell_box_du_mois_refuse">
          <span class="ltb-tun__radio" aria-hidden="true"></span>
          <span class="ltb-tun__option-texte">
            <span class="ltb-tun__option-nom">${jetons(t.pbNon)}</span>
            <span class="ltb-tun__option-detail">${jetons(t.pbNonTexte)}</span>
          </span>
        </button>
        ${m.bm && m.cal && t.calNote ? `<p class="ltb-tun__cal-note">${jetons(t.calNote)}</p>` : ''}`;
    }

    /* Le calendrier, troisième choix de l'étape. Son prix est celui du jour :
       −10 % seul, −15 % pour les deux quand la case du second est cochée —
       ce que la caisse appliquera. La case n'est pas dans le bouton (un
       contrôle dans un bouton n'est pas du HTML valide) : elle le suit, et
       ne paraît qu'une fois le calendrier choisi. */
    htmlChoixCalendrier(m) {
      const cal = m.cal;
      if (!cal) return '';
      const t = this.d.textes;
      const jetons = (texte) => this.jetons(texte, m);
      const choisi = this.etat.calendrier === true;
      const lignes = this.lignesCalendrier(cal);
      const premier = lignes[0];
      const precommande = cal.quand && t.calPrecommande
        ? `<span class="ltb-precom">${jetons(t.calPrecommande)}</span>` : '';
      let second = '';
      if (choisi && cal.autre) {
        const tauxDuo = this.d.calRemiseDuo;
        const promoAutre = Math.round((cal.autre.prix * (100 - tauxDuo)) / 100);
        second = `
        <label class="ltb-tun__cal-plus">
          <input class="ltb-tun__cal-plus-case" type="checkbox" data-tun-cal-autre${this.etat.calAutre ? ' checked' : ''}>
          <span class="ltb-tun__cal-plus-coche" aria-hidden="true">
            <svg width="12" height="12" viewBox="0 0 12 12" fill="none"><path d="M2.5 6.2l2.3 2.3 4.7-5" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/></svg>
          </span>
          ${cal.autre.image ? `<img class="ltb-tun__cal-plus-visuel" src="${cal.autre.image}" alt="" loading="lazy" width="56" height="56">` : ''}
          <span class="ltb-tun__cal-plus-texte">
            <span class="ltb-tun__cal-plus-nom">${jetons(t.calAutre)}</span>
            ${t.calAutreTexte ? `<span class="ltb-tun__cal-plus-detail">${jetons(t.calAutreTexte)}</span>` : ''}
          </span>
          <span class="ltb-tun__cal-plus-prix">${euro(promoAutre)}
            <s class="ltb-tun__option-plein">${euro(cal.autre.prix)}</s>
          </span>
        </label>`;
      }
      return `
        <button type="button" class="ltb-tun__option" role="radio"
          aria-checked="${choisi}"
          data-tun-choix="premiere" data-tun-valeur="calendrier" data-track="upsell_calendrier_accepte">
          <span class="ltb-tun__radio" aria-hidden="true"></span>
          ${cal.image ? `<img class="ltb-tun__vignette" src="${cal.image}" alt="" loading="lazy" width="88" height="88">` : ''}
          <span class="ltb-tun__option-texte">
            ${t.calKicker ? `<span class="ltb-tun__option-kicker">${jetons(t.calKicker)}</span>` : ''}
            <span class="ltb-tun__option-nom">${jetons(t.calOui)}</span>
            ${t.calAccroche ? `<span class="ltb-tun__option-accroche">${jetons(t.calAccroche)}</span>` : ''}
            ${t.calDetail ? `<span class="ltb-tun__option-detail">${jetons(t.calDetail)}</span>` : ''}
          </span>
          <span class="ltb-tun__option-prix">${euro(premier.promo)}
            <span class="ltb-tun__option-avant">
              <s class="ltb-tun__option-plein">${euro(premier.prix)}</s>
              <span class="ltb-tun__option-remise">−${premier.taux} %</span>
            </span>
            ${precommande}
          </span>
        </button>${second}`;
    }

    rendreCarte() {
      const t = this.d.textes;
      const formats = this.formatsDisponibles();
      const prix = formats.map((x) => x.prix);
      const plancher = prix.length ? Math.min.apply(null, prix) : 0;

      this.el.querySelector('[data-tun-formats]').innerHTML = formats.map((f) => {
        const sup = f.prix - plancher;
        // « à partir de 36,00 € » tient à droite d'un titre sur un écran large,
        // pas sur 390 px : il y écrasait la description du coffret sur cinq
        // lignes de trois mots. Un prix long prend sa propre ligne.
        const prixTexte = this.prixDuFormat(f, sup);
        const long = prixTexte.replace(/<[^>]*>/g, '').trim().length > 10;
        return `
        <button type="button" class="ltb-tun__option" role="radio"
          aria-checked="${this.etat.format === f.option}"
          data-tun-choix="format" data-tun-valeur="${echapper(f.option)}" data-track="${echapper(f.track || '')}">
          <span class="ltb-tun__radio" aria-hidden="true"></span>
          <span class="ltb-tun__option-texte">
            <span class="ltb-tun__option-nom">${f.titre || f.option}</span>
            <span class="ltb-tun__option-detail">${f.texte || ''}</span>
          </span>
          <span class="ltb-tun__option-prix${long ? ' ltb-tun__option-prix--long' : ''}">${prixTexte}</span>
        </button>`;
      }).join('');

      const courant = this.formatCourant();
      const zone = this.el.querySelector('[data-tun-coffret]');
      const boxes = this.d.coffretBoxes || [];
      zone.hidden = !(courant && courant.coffret && boxes.length);
      if (zone.hidden) return;

      zone.querySelector('[data-tun-coffret-boxes]').innerHTML = boxes.map((b) => `
        <button type="button" class="ltb-tun__coffret-box" role="radio"
          aria-checked="${String(this.etat.coffret) === String(b.id)}"
          data-tun-choix="coffret" data-tun-valeur="${b.id}" data-track="coffret_choix_box">
          <img class="ltb-tun__coffret-visuel" src="${b.image}" alt="" loading="lazy" width="160" height="160">
          <span class="ltb-tun__coffret-nom">${b.court || b.titre}</span>
          <span class="ltb-tun__coffret-prix">
            ${euro(b.promo)}
            <s class="ltb-tun__coffret-plein">${euro(b.prix)}</s>
          </span>
        </button>`).join('');

      /* Ce que contient le coffret retenu. Les vignettes se ressemblent et
         ne disent pas pourquoi l'une vaut dix euros de plus : la réponse est
         dans `akrolab.tags`, où un coffret à accessoires annonce sa cuillère
         et son infuseur. Une ligne, sous les vignettes, au moment du choix —
         plutôt que quatre lignes sur chaque carte, qui rendraient la grille
         illisible et le tunnel plus long. */
      const detail = zone.querySelector('[data-tun-coffret-detail]');
      if (!detail) return;
      const choisi = boxes.filter((b) => String(b.id) === String(this.etat.coffret))[0];
      const items = (choisi && choisi.contenu) || [];
      if (!choisi || !items.length) {
        detail.innerHTML = choisi ? '' : echapper(t.coffretDetailVide || '');
        return;
      }
      const intitule = t.coffretDetailLabel ? `${echapper(t.coffretDetailLabel)} : ` : '';
      detail.innerHTML = `<b class="ltb-tun__coffret-detail-cle">${intitule}</b>${items.map(echapper).join(' · ')}`;
    }

    /* Ce que le format ajoute vraiment à la formule. Le coffret ne se résume
       plus à son supplément de variante : son contenu est facturé à part, et
       il varie. Tant que rien n'est choisi on annonce donc le plancher — le
       contenu le moins cher du moment — et le montant exact dès qu'une box
       est retenue. Ajouter un contenu meilleur marché fait baisser l'annonce
       toute seule. */
    prixDuFormat(format, supplement) {
      const t = this.d.textes;
      if (!format.coffret) {
        return supplement > 0 ? `+ ${euro(supplement)}` : (t.offert || 'Offert');
      }
      const contenus = (this.d.coffretBoxes || []).map((b) => b.promo);
      if (!contenus.length) {
        return supplement > 0 ? `+ ${euro(supplement)}` : (t.offert || 'Offert');
      }
      const choisi = this.etat.format === format.option ? this.contenuCourant() : null;
      if (choisi) return `+ ${euro(supplement + choisi.promo)}`;
      const plancher = Math.min.apply(null, contenus);
      return `${t.aPartirDe || 'à partir de'} ${euro(supplement + plancher)}`;
    }

    /* Les champs ne sont pas reconstruits : ils sont dans le document, on ne
       fait que leur rendre ce qui a été saisi. Recréer les `input` à chaque
       rendu ferait perdre le curseur à la frappe. */
    rendreMessage() {
      this.el.querySelectorAll('[data-tun-champ]').forEach((champ) => {
        const garde = this.etat.champs[champ.dataset.tunChamp];
        if (garde !== undefined && champ.value !== garde) champ.value = garde;
      });
    }

    rendreLignes(m) {
      const t = this.d.textes;
      const lignes = [];
      if (m.cadeau) {
        lignes.push([`${t.abonnementLigne} ${m.cycle} mois`, euro(m.base)]);
        lignes.push([
          (t.cadeauLivraisonLigne || 'Livraison des [n] box').split('[n]').join(String(m.cycle)),
          euro(m.livraison),
        ]);
        const f = this.formatCourant();
        // Le panneau nomme le format choisi, il ne se contente pas d'en donner
        // le prix. Depuis que le coffret ne coûte plus de supplément, la ligne
        // affichait « Format du cadeau — Offert » juste au-dessus des 16 € de
        // la box qu'il contient : deux lignes qui se contredisaient à l'œil.
        // Le nom lève l'ambiguïté, et le supplément le suit quand il existe —
        // sans quoi les lignes ne feraient plus la somme annoncée.
        if (f) {
          const nom = f.court || f.titre || f.option;
          lignes.push([t.recapFormat, m.supplement > 0 ? `${nom} + ${euro(m.supplement)}` : nom]);
        }
        if (m.contenu) {
          lignes.push([m.contenu.court || m.contenu.titre, euro(m.contenu.promo)]);
        }
        if (m.envoi) lignes.push([this.libelleEnvoi(f), euro(m.envoi.port)]);
        const html = lignes.map(([g, d]) => `<li class="ltb-tun__ligne"><span>${g}</span><span>${d}</span></li>`).join('');
        this.el.querySelectorAll('[data-tun-lignes]').forEach((ul) => { ul.innerHTML = html; });
        return;
      }
      if (m.prepaye) {
        lignes.push([`${t.abonnementLigne} ${m.cycle} mois`, euro(this.formule.base)]);
        lignes.push([`${t.livraisonLigne} × ${m.cycle} mois`, euro(m.port * m.cycle)]);
      } else {
        lignes.push([t.abonnementLigne, `${euro(this.formule.base)}/mois`]);
        lignes.push([t.livraisonLigne, `${euro(m.port)}/mois`]);
      }
      // La ligne de la box du mois n'apparaît qu'une fois le choix fait :
      // aucune option payante n'est pré-cochée. Son port la suit, sans quoi
      // le panneau annoncerait un total que le récapitulatif démentirait.
      if (this.etat.premiere === true && m.bm) {
        lignes.push([`${m.bm.titre} −${this.d.remise} %`, euro(m.bm.promo)]);
        lignes.push([t.livraisonBoxLigne, this.prixPortBox(m)]);
      }
      if (m.calPris) {
        m.calLignes.forEach((l) => lignes.push([`${l.titre} −${l.taux} %`, euro(l.promo)]));
        lignes.push([this.libelleLivraisonCal(m), this.prixPort(m.calLiv)]);
      }
      const html = lignes.map(([g, d]) => `<li class="ltb-tun__ligne"><span>${g}</span><span>${d}</span></li>`).join('');
      this.el.querySelectorAll('[data-tun-lignes]').forEach((ul) => { ul.innerHTML = html; });
    }

    /* Deux colis quand la box du mois est prise : ils partent à des dates
       différentes et chacun porte sa livraison. Regrouper par colis est ce que
       fait déjà le tunnel de la fiche box — c'est aussi la seule présentation
       où les deux lignes de port ne se lisent pas comme un doublon. Sans la
       box, un seul colis : l'en-tête disparaît plutôt que d'annoncer un
       « colis 2 » qui n'aurait pas de premier. */
    rendreRecap(m) {
      const t = this.d.textes;
      const v = this.variante();
      const f = this.formule;
      if (m.cadeau) return this.rendreRecapCadeau(m, v, f);
      const prise = this.etat.premiere === true && !!m.bm;

      const abonnement = [
        [t.recapBox, `${f.nomBox} · ${f.titre}`, m.prepaye ? euro(f.base) : `${euro(f.base)}/mois`],
        [t.recapLivraison, `${this.etat.pays} · ${v ? v.mode : ''}`,
          m.prepaye ? euro(m.port * m.cycle) : `${euro(m.port)}/mois`, t.livraisonIncluseNote],
        [t.recapPremiere, this.jetons(t.recapPremiereValeur, m), this.jetons(t.recapPremiereMois, m)],
      ];

      if (m.calPris) {
        const groupesCal = [
          {
            // Un seul colis pour les calendriers, à la date de celui qui est
            // en précommande — la règle de Logvad, et celle de la fiche.
            colis: m.cal.quand ? this.jetons(t.colisCal, m) : t.colisCalStock,
            lignes: [
              ...m.calLignes.map((l) => [`${l.titre} à −${l.taux} %`, '', euro(l.promo), `${t.auLieuDe || 'au lieu de'} ${euro(l.prix)}`]),
              [this.libelleLivraisonCal(m), m.calLiv.note, this.prixPort(m.calLiv)],
            ],
          },
          { colis: this.jetons(t.colisAbo, m), lignes: abonnement },
        ];
        this.el.querySelector('[data-tun-recap]').innerHTML = this.htmlColis(groupesCal);
        return;
      }

      const groupes = prise
        ? [
          {
            // Une box vendue à découvert ne part pas sous 48 h : l'en-tête
            // du colis le dit, comme la vignette de l'étape précédente.
            colis: this.jetons(m.bm.precommande ? (t.colisBoxPrecommande || t.colisBox) : t.colisBox, m),
            lignes: [
              // Le délai d'expédition est déjà en tête de colis : le répéter
              // sous la ligne ferait lire deux fois la même information.
              [`${m.bm.titre} à −${this.d.remise} %`, '', euro(m.bm.promo)],
              [t.livraisonBoxLigne, m.boxLiv.note, this.prixPortBox(m)],
            ],
          },
          { colis: this.jetons(t.colisAbo, m), lignes: abonnement },
        ]
        : [{ colis: '', lignes: abonnement }];

      this.el.querySelector('[data-tun-recap]').innerHTML = this.htmlColis(groupes);
    }

    libelleLivraisonCal(m) {
      const t = this.d.textes;
      return m.calLignes.length > 1 ? t.livraisonCalLignes : t.livraisonCalLigne;
    }

    /* Un groupe, son en-tête, ses lignes. Les deux parcours s'en servent : le
       jour où la mise en forme d'une ligne bouge, elle bouge des deux côtés. */
    htmlColis(groupes) {
      return groupes.map((g) => `
        ${g.colis ? `<li class="ltb-tun__recap-colis">${g.colis}</li>` : ''}
        ${g.lignes.map(([label, valeur, prix, note]) => `
          <li class="ltb-tun__recap-ligne">
            <span>
              <span class="ltb-tun__recap-label">${label}</span>
              ${valeur ? `<span class="ltb-tun__recap-valeur">${valeur}</span>` : ''}
              ${note ? `<span class="ltb-tun__recap-note">${note}</span>` : ''}
            </span>
            <span class="ltb-tun__recap-prix">${prix}</span>
          </li>`).join('')}`).join('');
    }

    /* Un cadeau, c'est deux envois qui ne partent ni au même moment ni au
       même endroit : le coffret ou la carte imprimée quitte nos bureaux sous
       48 h vers l'adresse saisie au paiement, les box ne partiront qu'une fois
       la carte activée, vers l'adresse que le bénéficiaire donnera lui-même.
       C'est la confusion la plus coûteuse du parcours cadeau — d'où le même
       groupement par colis que le tunnel « pour moi », dans l'ordre où les
       choses arrivent. L'activation n'est plus une ligne à part : elle explique
       la livraison des box, elle se lit donc sous elle. */
    rendreRecapCadeau(m, v, f) {
      const t = this.d.textes;
      const format = this.formatCourant();

      const cadeau = [];
      if (format) {
        // « Offert » ne se dit que d'un format qui n'engage aucune dépense.
        // Le coffret n'a pas de supplément non plus — sa box est facturée à
        // part —, mais le choisir oblige à prendre cette box : l'annoncer
        // « Offert » juste au-dessus de ses 16,00 € se lisait « le coffret est
        // gratuit ». Quand une box l'accompagne, la colonne reste vide et
        // c'est la ligne suivante qui porte le montant. La ligne du format
        // dit alors ce qu'elle est : le format retenu, pas une dépense.
        let prixFormat = '';
        if (m.supplement > 0) {
          prixFormat = euro(m.supplement);
        } else if (!m.contenu) {
          prixFormat = t.offert || 'Offert';
        }
        cadeau.push([
          t.recapFormat,
          format.court || format.titre || format.option,
          prixFormat,
        ]);
      }
      if (m.contenu) {
        cadeau.push([
          t.coffretContenuLigne,
          // Le nom court : « Box Essentielle - Le Jardin Secret » redit une
          // famille que la ligne du dessus a déjà nommée.
          `${m.contenu.court || m.contenu.titre} ${t.coffretSuffixe || ''}`.trimEnd(),
          `${euro(m.contenu.promo)}`,
          // Le pourcentage ne se dit plus : la remise est un montant fixe, et
          // il ne vaut pas le même taux d'un contenu à l'autre. Le prix
          // d'avant, lui, est vrai pour tous.
          `${t.auLieuDe || 'au lieu de'} ${euro(m.contenu.prix)}`,
        ]);
      }
      if (m.envoi) {
        // Le délai d'expédition est dans l'en-tête du colis : le répéter sous
        // la ligne ferait lire deux fois la même phrase.
        cadeau.push([
          this.libelleEnvoi(format),
          `${this.etat.pays} · ${m.envoi.mode}`,
          euro(m.envoi.port),
        ]);
      }

      const abonnement = [
        [t.recapFormule, `${f.titre} — ${f.nomBox}`, euro(m.base)],
        [t.recapLivraisonBenef, `${this.etat.pays} · ${v ? v.mode : ''} · ${m.cycle} box`,
          euro(m.livraison), t.recapActivationNote],
      ];

      // Une carte par e-mail ne fait pas de colis : l'en-tête le dit autrement
      // plutôt que d'annoncer un envoi qui n'existe pas.
      const part = !!(format && format.profil && format.profil !== 'aucun');
      const groupes = [];
      if (cadeau.length) groupes.push({ colis: part ? t.colisCadeau : t.colisCadeauEmail, lignes: cadeau });
      groupes.push({ colis: t.colisAboCadeau, lignes: abonnement });

      this.el.querySelector('[data-tun-recap]').innerHTML = this.htmlColis(groupes);
    }

    /* « Livraison du coffret » ou « Envoi de la carte » : deux objets, deux
       colis, deux mots — le client ne se demande pas lequel il paie. */
    libelleEnvoi(format) {
      const t = this.d.textes;
      return format && format.profil === 'coffret'
        ? (t.envoiCoffretLigne || 'Livraison du coffret')
        : (t.envoiCourrierLigne || 'Envoi de la carte');
    }

    /* ---------------- Ajout au panier ----------------

       Deux lignes au plus : l'abonnement avec son plan Recharge, et la box du
       mois à plein tarif. La remise de 30 % n'est pas appliquée ici — c'est
       une remise automatique de Shopify, déclenchée par la présence des deux
       lignes. L'appliquer dans le thème la ferait compter deux fois.

       Un cadeau, lui, tient en une seule ligne : le format de carte est une
       option de variante, pas un supplément à ajouter. Ne voyagent en
       propriétés que ce qui n'a pas de place ailleurs — le message et, pour
       le coffret, le thème de la box offerte. */

    /* Les noms `De`, `À`, `Message`, `Envoi` sont ceux de la page actuelle et
       du scénario Make qui lit les commandes : ils sont écrits dans le
       gabarit, jamais reconstruits ici. Un champ vide ne part pas — une
       propriété vide s'afficherait quand même sur la commande. */
    ligneCadeau() {
      const vr = this.varianteReelle();
      if (!vr) return [];
      const proprietes = {};
      this.el.querySelectorAll('[data-tun-champ]').forEach((champ) => {
        const valeur = (champ.value || '').trim();
        if (valeur) proprietes[champ.dataset.tunChamp] = valeur;
      });
      const format = this.formatCourant();
      const contenu = format && format.coffret ? this.contenuCourant() : null;
      if (contenu) {
        proprietes[this.d.textes.coffretPropriete || 'Box du coffret'] = contenu.titre;
      }
      const lignes = [{ id: vr.id, quantity: 1, properties: proprietes }];
      // Le contenu part à plein tarif : la remise de 30 % est une remise
      // automatique de Shopify, déclenchée par la présence des deux lignes.
      if (contenu) lignes.push({ id: contenu.id, quantity: 1 });
      return lignes;
    }

    async ajouterAuPanier() {
      const f = this.formule;
      const v = this.variante();
      const m = this.montants();
      if (!v) return;

      const bouton = this.el.querySelector('[data-tun-suivant]');
      // Le plan se prend sur la variante choisie, pas sur la formule : depuis
      // 2026 Recharge crée un groupe de plans par variante, et Shopify refuse
      // un plan qui ne couvre pas la variante ajoutée. `f.planId` reste le
      // repli pour les produits à plan unique, écrits avant cette bascule.
      const lignes = this.cadeau ? this.ligneCadeau() : [
        { id: v.id, quantity: 1, selling_plan: v.plan || f.planId },
      ];
      if (!lignes.length) return;
      if (!this.cadeau && this.etat.premiere === true && m.bm) lignes.push({ id: m.bm.id, quantity: 1 });
      // Les calendriers partent à plein tarif, comme la box du mois : les
      // remises « achetez X, obtenez Y » de la boutique se posent d'elles-mêmes.
      if (!this.cadeau && m.calPris) m.calLignes.forEach((l) => lignes.push({ id: l.id, quantity: 1 }));

      this.el.querySelectorAll('[data-tun-suivant]').forEach((b) => { b.disabled = true; });
      try {
        const reponse = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items: lignes }),
        });
        if (!reponse.ok) throw new Error(String(reponse.status));
        // Le repère de fin d'entonnoir part avant la navigation : `gtag`
        // passe par `sendBeacon`, qui survit au départ de la page. Le drapeau
        // évite qu'un `pagehide` compte en plus un abandon.
        this.converti = true;
        mesurer('ajout_au_panier', {
          value: Math.round(m.aujourdhui) / 100,
          currency: 'EUR',
          pays: this.etat.pays,
          lignes: lignes.length,
          premiere_box: this.cadeau ? undefined : this.etat.premiere === true,
          calendrier: m.calPris ? (m.calLignes.length > 1 ? 'deux' : 'un') : undefined,
          format_carte: this.cadeau ? this.etat.format : undefined,
          coffret_box: this.cadeau && m.contenu ? m.contenu.titre : undefined,
        });
        window.location.href = '/cart';
      } catch (e) {
        // Un ajout refusé est le pire des cas : le client a tout rempli et
        // repart les mains vides. Il se mesure, sinon il ne se voit nulle part.
        mesurer('ajout_au_panier_echec', { erreur: String(e && e.message || e).slice(0, 60) });
        this.el.querySelectorAll('[data-tun-suivant]').forEach((b) => { b.disabled = false; });
        if (bouton) bouton.textContent = 'Réessayer';
      }
    }
  }

  /* Rails fléchés — la rangée des box déjà parties. Le pas est celui d'une
     vignette et de sa gouttière, mesuré sur le rendu plutôt que codé en dur :
     la largeur change entre desktop et mobile. */
  const brancherRails = () => {
    document.querySelectorAll('[data-abo-rail-piste]').forEach((piste) => {
      if (piste.dataset.railPret) return;
      piste.dataset.railPret = '1';

      const zone = piste.closest(ZONES) || document;
      const majFleches = () => {
        const reste = piste.scrollWidth - piste.clientWidth;
        zone.querySelectorAll('[data-abo-rail]').forEach((btn) => {
          const sens = Number(btn.dataset.aboRail);
          btn.disabled = reste <= 1
            || (sens < 0 && piste.scrollLeft <= 1)
            || (sens > 0 && piste.scrollLeft >= reste - 1);
        });
      };

      zone.querySelectorAll('[data-abo-rail]').forEach((btn) => {
        btn.addEventListener('click', () => {
          const vignette = piste.firstElementChild;
          const pas = vignette ? vignette.getBoundingClientRect().width + 24 : 300;
          piste.scrollBy({ left: pas * 2 * Number(btn.dataset.aboRail), behavior: 'smooth' });
        });
      });

      piste.addEventListener('scroll', majFleches, { passive: true });
      window.addEventListener('resize', majFleches);
      majFleches();
    });
  };

  const demarrer = () => {
    brancherMesure();
    document.querySelectorAll('[data-ltb-abo]').forEach((racine) => {
      if (racine.dataset.aboPret) return;
      racine.dataset.aboPret = '1';
      new LtbAbo(racine);

      // L'arrivée sur la page se mesure ici plutôt que par une balise dans le
      // gabarit : les deux pages partagent le même document après une
      // bascule, et seul le script sait laquelle est affichée.
      mesurer('abo_page_vue');

      const balise = racine.querySelector('[data-ltb-abo-tunnel]');
      if (!balise) return;
      try {
        tunnelActif = new Tunnel(racine, JSON.parse(balise.textContent));
      } catch (e) {
        // Données de tunnel illisibles : la vitrine reste utilisable, seuls
        // les boutons de formule ne répondent plus. Mieux vaut ça qu'une page
        // blanche.
      }
    });
    brancherRails();

    // Une bande chargée après la vitrine — l'éditeur de thème le fait — doit
    // rattraper le mode courant plutôt que garder celui de son rendu.
    const courant = document.documentElement.dataset.ltbAboMode;
    if (courant) poserMode(courant);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', demarrer);
  } else {
    demarrer();
  }

  document.addEventListener('shopify:section:load', demarrer);
})();
